import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageShell } from '@/components/Layout';
import { V2Banner } from '@/components/V2Banner';
import { Device } from '@/lib/device';
import { useToast } from '@/lib/ui';
import { APP_VERSION } from '../version';

function ActionButton({ label, onClick, destructive }: { label: string; onClick: () => void; destructive?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`h-9 px-3 rounded-md text-sm font-medium border ${
        destructive ? 'border-destructive text-destructive' : 'border-border hover:bg-accent'
      }`}
    >
      {label}
    </button>
  );
}

function useV2Toast() {
  const toast = useToast();
  return () => toast.push('v2 — non implémenté', 'info');
}

// ─── Jobs → moved to pages/Jobs.tsx (live page). ─────────────────────────
// ─── Ingestion → moved to pages/Ingest.tsx (live page). ──────────────────
// ─── JSON (reception_json) → moved to pages/Json.tsx (live page). ────────
// ─── MQTT (traitement_mqtt) → moved to pages/Mqtt.tsx (live page). ───────

// ─── Users ───────────────────────────────────────────────────────────────
export function Users({ device }: { device: Device }) {
  const v2 = useV2Toast();
  const users = [
    { email: 'admin@meteor-oi.re', role: 'superAdmin', last: 'il y a 5 min', disabled: false },
    { email: 'nicolas@lighthouse-path.com', role: 'admin', last: 'il y a 2 h', disabled: false },
    { email: 'ancien@example.com', role: 'user', last: 'jamais', disabled: true },
  ];
  return (
    <PageShell device={device} title="Utilisateurs · 🔒 superAdmin" breadcrumb="Système">
      <div className="p-4 md:p-6 space-y-4">
        <ActionButton label="+ Créer utilisateur" onClick={v2} />
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2.5">email</th>
                <th className="text-left px-4 py-2.5">rôle</th>
                <th className="text-left px-4 py-2.5">dernière connexion</th>
                <th className="text-left px-4 py-2.5">status</th>
                <th className="w-32 px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((u) => (
                <tr key={u.email}>
                  <td className="px-4 py-2.5 text-xs">{u.email}</td>
                  <td className="px-4 py-2.5"><span className={`badge-${u.role === 'superAdmin' ? 'violet' : 'blue'} px-1.5 py-0.5 rounded text-[10px] font-medium`}>{u.role}</span></td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{u.last}</td>
                  <td className="px-4 py-2.5 text-xs">{u.disabled ? <span className="badge-slate px-1.5 py-0.5 rounded text-[10px]">désactivé</span> : <span className="badge-green px-1.5 py-0.5 rounded text-[10px]">actif</span>}</td>
                  <td className="px-4 py-2.5 text-right space-x-2">
                    <button className="text-xs text-primary hover:underline" onClick={v2}>Éditer</button>
                    <button className="text-xs text-destructive hover:underline" onClick={v2}>Kick</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PageShell>
  );
}

// ─── Settings ────────────────────────────────────────────────────────────
export function Settings({ device }: { device: Device }) {
  const v2 = useV2Toast();
  return (
    <PageShell device={device} title="Paramètres" breadcrumb="Système">
      <div className="p-4 md:p-6 space-y-4 max-w-2xl">
        <section className="bg-card border border-border rounded-lg p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Profil</h2>
          <div className="space-y-2 text-sm">
            <div>Email: <span className="font-mono">admin@meteor-oi.re</span></div>
            <div>Rôle: <span className="badge-violet px-1.5 py-0.5 rounded text-[10px] font-medium">superAdmin</span></div>
            <ActionButton label="Changer mot de passe" onClick={v2} />
          </div>
        </section>
        <section className="bg-card border border-border rounded-lg p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Sécurité</h2>
          <div className="space-y-2">
            <ActionButton label="Enregistrer Passkey (WebAuthn)" onClick={v2} />
            <ActionButton label="Activer 2FA (TOTP)" onClick={v2} />
          </div>
        </section>
        <section className="bg-card border border-border rounded-lg p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Notifications</h2>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" defaultChecked /> Email</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" /> Push mobile</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" defaultChecked /> Slack</label>
        </section>
        <section className="bg-card border border-border rounded-lg p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">À propos</h2>
          <div className="text-sm flex items-center justify-between">
            <span className="text-muted-foreground">Version</span>
            <span className="font-mono">{APP_VERSION}</span>
          </div>
        </section>
      </div>
    </PageShell>
  );
}

// ─── More (mobile fallback) ──────────────────────────────────────────────
export function More({ device }: { device: Device }) {
  const links = [
    { to: '/db', label: 'Base de données', icon: '📊' },
    { to: '/jobs', label: 'Jobs', icon: '⏱' },
    { to: '/ingest', label: 'Ingestion', icon: '⚡' },
    { to: '/json', label: 'Réception JSON', icon: '📥' },
    // Masqué tant que le service MQTT n'est pas exploité — cf. Sidebar.tsx.
    // { to: '/mqtt', label: 'Traitement MQTT', icon: '📡' },
    { to: '/audit', label: 'Audit', icon: '📋' },
    { to: '/users', label: 'Utilisateurs', icon: '👤' },
    { to: '/settings', label: 'Paramètres', icon: '⚙' },
  ];
  return (
    <PageShell device={device} title="Plus">
      <div className="divide-y divide-border">
        {links.map((l) => (
          <Link key={l.to} to={l.to} className="flex items-center gap-3 px-4 py-3 hover:bg-accent/30">
            <span className="text-xl">{l.icon}</span>
            <span className="font-medium">{l.label}</span>
          </Link>
        ))}
      </div>
    </PageShell>
  );
}
